import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import CardClinica from "../CardClinicas/CardClinicas";
import { cardClinicas } from "./dataClinicas";
import ImgClinicas from "../../assets/Page_1/1.4_Colaboraciones/Illustration.svg";
import MapClinicas from "../MapClinicas/MapClinicas";
import { getDistance } from "../../helper/getDistance";
import { getCoordinatesFromCP, DEFAULT_COORDS } from "./CpCoordinates";
import "./SectionClinicas.css";

const SectionClinicas = () => {
  const [searchParams] = useSearchParams();
  const hospitalClient = searchParams.get("hospital") || "Vertex_Lab_Direct";
  const testName = searchParams.get("test") || "";

  const [cp, setCp] = useState("");
  const [mapCenter, setMapCenter] = useState({ lat: 19.4326, lng: -99.1332 });
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [searchCoords, setSearchCoords] = useState(null);

  const isSearchActive = cp.length === 5;

  const handleCpSearch = (codigoPostal) => {
    setIsLoadingMap(true);

    // 1. Buscar si existe en base de datos local de clínicas
    const clinicaEncontrada = cardClinicas.find((c) => c.cp === codigoPostal);

    if (clinicaEncontrada) {
      const coords = {
        lat: clinicaEncontrada.lat,
        lng: clinicaEncontrada.lng
      };
      setSearchCoords(coords);
      setMapCenter(coords);
      setIsLoadingMap(false);
      return;
    }

    // 2. Buscar en el mapa de prefijos de CP
    const coordsFromCP = getCoordinatesFromCP(codigoPostal);

    if (coordsFromCP) {
      console.log(`📍 CP ${codigoPostal} → Región: ${coordsFromCP.region}`);

      const coords = { lat: coordsFromCP.lat, lng: coordsFromCP.lng };
      setSearchCoords(coords);

      // Encontrar la clínica más cercana para centrar el mapa
      const conDistancia = cardClinicas.map((c) => ({
        ...c,
        dist: getDistance(coords.lat, coords.lng, c.lat, c.lng)
      }));

      const masCercana = conDistancia.sort((a, b) => a.dist - b.dist)[0];
      setMapCenter({ lat: masCercana.lat, lng: masCercana.lng });
    } else {
      setSearchCoords(DEFAULT_COORDS);
      setMapCenter(DEFAULT_COORDS);
    }

    setIsLoadingMap(false);
  };

  // Clínicas ordenadas por distancia con la distancia incluida
  const clinicasOrdenadas = useMemo(() => {
    if (!searchCoords) {
      return cardClinicas.map((c) => ({ ...c, distancia: null }));
    }

    return [...cardClinicas]
      .map((c) => ({
        ...c,
        distancia: getDistance(searchCoords.lat, searchCoords.lng, c.lat, c.lng)
      }))
      .sort((a, b) => a.distancia - b.distancia);
  }, [searchCoords]);

  const cercanas = clinicasOrdenadas.slice(0, 3);
  const restantes = clinicasOrdenadas.slice(3);

  // Verificar si hay match exacto de CP
  const hasMatch = cardClinicas.some((clinica) => clinica.cp === cp);
  const showNotFoundMessage = isSearchActive && !hasMatch;

  const handleChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 5) {
      setCp(value);

      // Resetear cuando el usuario borra
      if (value.length < 5) {
        setSearchCoords(null);
      }

      if (value.length === 5) {
        handleCpSearch(value);
      }
    }
  };

  return (
    <section className="section-clinicas">
      <p className="description-clinicas">
        Ingresa tu Código Postal para recomendarte la opción más cercana a ti
      </p>
      <div className="cp-wrapper">
        <div className="cp-container">
          <label htmlFor="cp-input" className="cp-label">
            Ingresa tu C.P
          </label>
          <input
            id="cp-input"
            type="text"
            className="cp-field"
            value={cp}
            onChange={handleChange}
            placeholder="00000"
            inputMode="numeric"
            autoComplete="postal-code"
          />
        </div>
      </div>

      {/* --- RENDERIZADO CONDICIONAL --- */}
      {!isSearchActive && (
        <div className="initial-state-container">
          <img
            src={ImgClinicas}
            alt="Ilustración clínica"
            className="img-clinicas"
          />
          <p className="description-clinicas" style={{ marginTop: "1rem" }}>
            Empieza escribiendo tu código postal arriba para ver resultados.
          </p>
        </div>
      )}

      {isSearchActive && (
        <>
          <div
            style={{
              marginBottom: "2rem",
              marginTop: "1rem",
              display: "contents"
            }}
          >
            <MapClinicas
              center={mapCenter}
              isLoading={isLoadingMap}
              clinicas={clinicasOrdenadas}
            />
          </div>

          {showNotFoundMessage && (
            <div className="not-found-message">
              <p>
                <strong>📍 No encontramos una clínica en el C.P. {cp}.</strong>
                <br />
                Pero no te preocupes, aquí tienes todas nuestras sucursales
                ordenadas por cercanía. ¡Seguro alguna te queda cómoda! 👇
              </p>
            </div>
          )}

          <h1 className="title-clinicas">
            {hasMatch ? "Tu clínica más cercana" : "Profesionales cerca de ti"}
          </h1>

          <p className="description-clinicas">
            Tu tranquilidad está garantizada con profesionales que realmente se
            preocupan por tu visión.
          </p>

          {/* Sección de resultados */}
          <div className="clinicas-container">
            <h2 className="subtitle-clinicas">Las 3 opciones más cercanas</h2>
            <div className="clinicas-grid">
              {cercanas.map((clinica) => (
                <CardClinica
                  key={clinica.id}
                  clinica={clinica}
                  highlight={true}
                  testName={testName}
                  hospitalClient={hospitalClient}
                />
              ))}
            </div>

            {restantes.length > 0 && (
              <>
                <hr
                  className="divider-clinicas"
                  style={{ margin: "3rem 0", opacity: 0.2 }}
                />
                <h2 className="subtitle-clinicas">
                  Otras sucursales que podrían interesarte
                </h2>
                <div className="clinicas-grid" style={{ opacity: 0.8 }}>
                  {restantes.map((clinica) => (
                    <CardClinica
                      key={clinica.id}
                      clinica={clinica}
                      testName={testName}
                      hospitalClient={hospitalClient}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default SectionClinicas;
